import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Box,
  useTheme,
} from "@mui/material";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../../lib/axios";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

const MultiAssignDialog = ({
  open,
  onClose,
  tickets,
  propName,
  assignBy,
  setSelectedRows,
  setSelectedCategoryId,
  fetchCardData,
  fetchTickets
}) => {
  const theme = useTheme();
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");

  useEffect(() => {
    if (open && tickets.length > 0) {
      const fetchServiceProviders = async () => {
        try {
          const multiTicketIds = tickets.map((ticket) => ticket.TicketId).join(",");
          const multiCategoryIds = [
            ...new Set(tickets.map((ticket) => ticket.TktCategoryId)),
          ].join(",");
          const propId = tickets[0].PropId;
          const response = await axiosInstance.post(
            "/TrnTicketing/TrnTicketServPrdDrp",
            {
              TicketId: 1,
              TicketSt: "",
              Flag: "ServPrdDrpTrnTkt",
              MultiTktCategoryId: multiCategoryIds,
              MultiTicketId: multiTicketIds,
              PropId: propId,
            }
          );
          const { Status, Data } = response.data;
          if (Status === 0 && Array.isArray(Data) && Data.length > 0) {
            setServiceProviders(Data);
            setSelectedProvider(Data[0].ServiceProvId);
          } else {
            setServiceProviders([]);
            setSelectedProvider("");
            toast.warn("No service providers available.", { autoClose: 1000 });
          }
        } catch (error) {
          console.error("Error fetching service providers:", error);
          setServiceProviders([]);
          setSelectedProvider("");
          toast.error("Error fetching service provider. Please try again.", {
            autoClose: 1000,
          });
        }
      };
      fetchServiceProviders();
    }
  }, [open, tickets]);

  const handleSubmit = async () => {
    if (!selectedProvider) {
      toast.warn("Please select a service provider.", {
        position: "top-center",
        autoClose: 1500,
      });
      return;
    }

    try {
      const payload = {
        ManageServProUpdation: tickets.map((ticket) => ({
          TicketId: ticket.TicketId,
          ForwardedId: selectedProvider,
          Flag: "",
        })),
      };

      // Stringify, escape, and wrap in quotes like reference code
      let jsonData = JSON.stringify(payload);
      jsonData = jsonData.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      jsonData = '"' + jsonData + '"';

      console.log("Submitting payload:", jsonData);

      const response = await axiosInstance.post(
        "/TrnTicketing/ManageServProUpdation",
        jsonData,
      );

      const { Status, Message } = response.data;
      console.log("API response:", response.data); // Debug response

      if (Status === 0) {
        toast.success(Message || "Tickets assigned successfully.", {
          position: "top-center",
          autoClose: 1500,
        });
        fetchTickets();
        fetchCardData();
        onClose();
      } else {
        toast.error(Message || "Failed to assign tickets.", {
          position: "top-center",
          autoClose: 1500,
        });
      }
    } catch (error) {
      console.error("Error assigning tickets:", error);
      toast.error(
        error.response?.data?.Message || "Error assigning tickets. Please try again.",
        {
          position: "top-center",
          autoClose: 1500,
        }
      );
    }
  };

  const handleCancel = () => {
    if (typeof setSelectedRows === "function") {
      setSelectedRows([]);
    }
    if (typeof setSelectedCategoryId === "function") {
      setSelectedCategoryId(null);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: 6,
      },
    }}>
      <DialogTitle sx={{
        pt: 1,
        pb: 1,
        px: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: { xs: '1.2rem', sm: '1.6rem' },
        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: '#fff',
      }}>Assign Multiple Tickets</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {/* Property */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationCityIcon color="primary" fontSize="small" />
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Property:
            </Typography>
            <Typography variant="body1">{propName || "N/A"}</Typography>
          </Box>

          {/* Assign By */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon color="primary" fontSize="small" />
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Assign By:
            </Typography>
            <Typography variant="body1">{assignBy || "N/A"}</Typography>
          </Box>

          {/* Assign To Dropdown */}
          {/* Assign To Dropdown - Stay in One Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.2,
              flexWrap: 'nowrap', // ensure same row
              flexDirection: { xs: 'row', sm: 'row' }, // responsive, but horizontal always
            }}
          >
            <AssignmentIndIcon color="primary" fontSize="small" />
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, minWidth: '85px', whiteSpace: 'nowrap' }}
            >
              Assign To:
            </Typography>
            <FormControl
              size="small"
              sx={{
                flex: 1, 
                minWidth: 0,
              }}
            >
              <Select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                displayEmpty
                renderValue={(value) => {
                  const provider = serviceProviders.find(
                    (prov) => prov.ServiceProvId === value
                  );
                  return provider
                    ? provider.ServiceProvider
                    : 'Select Service Provider';
                }}
                sx={{
                  fontSize: '1rem',
                  height: '36px',
                }}
              >
                {serviceProviders.length === 0 && (
                  <MenuItem value="" disabled>
                    No Service Providers Available
                  </MenuItem>
                )}
                {serviceProviders.map((prov) => (
                  <MenuItem
                    key={prov.ServiceProvId}
                    value={prov.ServiceProvId}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {prov.ServiceProvider}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>


        </Box>
      </DialogContent>


      <DialogActions sx={{ mr: { xs: '0px', sm: '0px', md: '15px' } }}>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!selectedProvider}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            px: 4,
            fontSize: { xs: '0.99rem', sm: '1rem' },
          }}
        >
          Submit
        </Button>
        <Button onClick={handleCancel}
          color="error"
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            px: 4,
            fontSize: { xs: '0.99rem', sm: '1rem' },
          }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiAssignDialog;